import create, { UseStore } from 'zustand'
import { EmCategoryApi, emCategoryApi, EmCategoryForm } from '../components/em-calculator/em-category-api'
import { fetchRequest, FormApi } from './'
import { EmElements, EmLevel, EmResult, emResultsFromProcedures, Procedure } from './domain'
import { log } from './log'
import { CriticalCare } from './procedure'
import { worksheetMessage, WorksheetResult } from './worksheet-service'
export interface Driver {
  driverName: string
  driverDescription: string
  supportsNOPP: boolean
  noppList: any[]
  supportsTimeBased: boolean
  timeRangeList: string[]

  minHPI: number
  maxHPI: number
  minROS: number
  maxROS: number
  minPFSH: number
  maxPFSH: number
  minEXAM: number
  maxEXAM: number
  minOPT: number
  maxOPT: number
  minDATA: number
  maxDATA: number
  minRISK: number
  maxRISK: number

  labelsOPT: string[]
  labelsDATA: string[]
  labelsRISK: string[]

  isDefault?: boolean
}

export interface NoteType {
  noteType: string
  noteDescription: string
  drivers: Driver[]
  isDefault?: boolean
}

export interface EmCalculatorConfiguration {
  errorMessage: string
  department: string
  supportedNotesAndDrivers: NoteType[]
  noteTypes: any
  minHPI: number
  maxHPI: number
  minROS: number
  maxROS: number
  minPFSH: number
  maxPFSH: number
  minEXAM: number
  maxEXAM: number
  minOPT: number
  maxOPT: number
  minDATA: number
  maxDATA: number
  minRISK: number
  maxRISK: number
  interventions?: EmLevel[]
  severities?: EmLevel[]
}
export interface EmRequest {
  hpi?: number
  ros?: number
  pfsh?: number
  exam?: number
  opt?: number
  data?: number
  risk?: number
  department?: string
  noteType?: string
  emDriver?: string
  noppCategory?: string
  timeRange?: string
  totalCriticalCareTime?: number
  isMedicare?: boolean
}

export function emRequest(obj?: Partial<EmRequest>): EmRequest {
  return {
    ...{
      hpi: null,
      ros: null,
      pfsh: null,
      exam: null,
      opt: null,
      data: null,
      risk: null,
      department: null,
      noteType: null,
      emDriver: null,
      noppCategory: null,
      timeRange: null,
    },
    ...obj,
  }
}

type EmConfigResult = WorksheetResult<EmCalculatorConfiguration>
interface Department {
  department: string
}

type EmCalcResult = WorksheetResult<EmResult>

export const calculateFromElementsRequest = fetchRequest<EmRequest, EmCalcResult>({
  method: 'post',
  body: (args: EmRequest) => worksheetMessage(args, 'calculateemfromelements'),
})

export const calculateFromTimeRequest = fetchRequest<EmRequest, EmCalcResult>({
  method: 'post',
  body: (args: EmRequest) => worksheetMessage(args, 'calculateemfromtime'),
})

type ElementRequest = {
  department: string
  noteType: string
  emDriver: string
  opt: string
  data: string
  risk: string
  totalCriticalCareTime?: number
  isMedicare?: boolean
}
export const calculateFromElementsRequest2 = fetchRequest<ElementRequest, EmCalcResult>({
  method: 'post',
  body: (args: ElementRequest) => worksheetMessage(args, 'calculateemfromelements', 'em-calculator'),
})

const configByDepartment: Record<string, EmCalculatorConfiguration> = {}

export const getEmCalculatorConfigRequest2 = fetchRequest({
  method: 'post',
  // TODO: enable caching once stable
  // cacheResult: ({ department }: Department) => configByDepartment[department],
  // onResult: ({ department }: Department, value) => value?.success && (configByDepartment[department] = value),
  body: (args: Department & { locationCode: string }) =>
    worksheetMessage(args, 'getemcalculatordepartmentconfig', 'em-calculator'),
})



 type EmCalculatorApi = {
  categories: EmCategoryApi
  config: EmCalculatorConfiguration
  config2: EmCalculatorConfiguration
  request: EmRequest
  department: string
  result: EmResult
  reset: boolean
  update: boolean
  criticalCare: CriticalCare
  inputs: EmResult
  currentNonCritical: Procedure[]
  readonly: boolean
  required: boolean
  v2: boolean
  open: boolean
  serviceCategoryForm: FormApi
  criticalCareForm: FormApi
  updateInterventions?: (a: EmElements) => void
  initialized(): boolean
  loadConfig(locationCode: string, department: string): void
  setRequest(value: EmRequest): void
  setProcedure(value: Procedure): void
  updateMinMax: (d: Driver) => void
  calculateElements(value?: EmRequest): void
  calculateTime(value?: EmRequest): void
  calculateNopp(value?: EmRequest): void
  setResult: (a: Partial<EmResult>) => void
  clear(): void
}

function isValidRequest(r: EmRequest) {
  if (!r || !r.department || !r.noteType || !r.emDriver) {
    return false
  }
  return true
}
function isValidElementRequest(r: EmRequest) {
  if (r.department !== 'URGENTCARE') {
    return (
      isValidRequest(r) &&
      !isNaN(r.hpi) &&
      !isNaN(r.ros) &&
      !isNaN(r.pfsh) &&
      !isNaN(r.exam) &&
      !isNaN(r.opt) &&
      !isNaN(r.data) &&
      !isNaN(r.risk)
    )
  }
  return isValidRequest(r) && !isNaN(r.opt) && !isNaN(r.data) && !isNaN(r.risk)
}

function isValidTimeRangeRequest(r: EmRequest) {
  return isValidRequest(r) && (r.timeRange || r.noppCategory)
}

export type EmCalculatorStore = UseStore<EmCalculatorApi> & {
  onResult?: (a: EmResult) => void
}
export function createEmCalculatorApi(): EmCalculatorStore {
  const configMap = {}

  const categories = emCategoryApi({
    onCalculate: (a: EmCategoryForm<string>) => {
      console.info('onCalculate', a)
      const request = api.getState().request
      request.opt = a.opt as any
      request.data = a.data as any
      request.risk = a.risk as any
      api.getState().calculateElements(a as any)
    },
  })

  const api: EmCalculatorStore = create<EmCalculatorApi>((set, get) => ({
    categories,
    config: null,
    config2: null,
    department: null,
    request: { department: null },
    result: null,
    criticalCare: null,
    inputs: null,
    reset: true,
    update: false,
    currentNonCritical: [],
    readonly: false,
    required: false,
    v2: false,
    open: true,
    serviceCategoryForm: null,
    criticalCareForm: null,
    initialized: () => !!get().config || !!get().config2,
    setRequest: (v) => {
      set({ result: null, request: { ...get().request, ...v } })
    },
    loadConfig: async (locationCode, department) => {
      set({ result: null })

      console.info('loading em calculator config... v2', get().v2, department)

      if (!department) {
        throw new Error('department is required for em configuration')
      }

      if (get().v2) {
        await getEmCalculatorConfigRequest2({
          locationCode,
          department,
        })
          .then((r) => {
            if (r?.success && r?.result) {
              set({ config2: r?.result })
            }
          })
          .catch((err) => log.error(err))
      }

      //TODO test in clear data environment
      set({ department })

      if (!configMap[department]) {
        const response: EmConfigResult = await getEmCalculatorConfigRequest2({
          department,
          locationCode,
        })

        if (response.success && response?.result) {
          const config = response.result
          config.minHPI = 1
          config.maxHPI = 8

          config.minROS = 0
          config.maxROS = 14

          config.minPFSH = 0
          config.maxPFSH = 3

          config.minEXAM = 1
          config.maxEXAM = 12

          config.minOPT = 1
          config.maxOPT = 4

          config.minDATA = 0
          config.maxDATA = 10

          config.minRISK = 1
          config.maxRISK = 4

          configMap[department] = config

          set({ config })
        } else {
          console.log('em config not available for department ', department, 'using test config')
          configMap[department] = null
          set({ config: configMap[department] })
        }
      }
    },
    setProcedure(cpt: Procedure) {
      const conf = get().config2
      if (conf && cpt?.isEMCode) {
        const res = emResultsFromProcedures([cpt])[0]
        if (res) {
          const form = get().serviceCategoryForm

          if (form) {
            const { noteType, emDriver, noppCategory } = form.controls
            console.info('setProcedure', res, noteType, emDriver, noppCategory)
            if (res.noteType) {
              noteType.setListValue((o: { noteType: string }) => o.noteType === res.noteType)
            }
            if (res.emDriver) {
              emDriver.setListValue((o: { driverName: string }) => o.driverName === res.emDriver)
            }
            if (res.noppCategory) {
              noppCategory.setListValue((o: { value: string }) => o.value === res.noppCategory)
            }
          }
          setTimeout(() => {
            const nt = conf.supportedNotesAndDrivers.find((n) => n.noteType === res.noteType)
            if (nt) {
              const dr = nt.drivers.find((d) => d.driverName === res.emDriver)

              if (dr) {
                const api = get().categories
                api.setDriver(dr)

                api.set({
                  opt: { label: res.optLevel, value: res.optLevel },
                  data: { label: res.dataLevel, value: res.dataLevel },
                  risk: { label: res.riskLevel, value: res.riskLevel },
                })
              }
            }
            const crit = get().criticalCareForm
            if (crit) {
              const { totalCriticalCareTime, isMedicare } = crit.controls
              totalCriticalCareTime.value.setState({ value: res.totalCriticalCareTime })
              isMedicare.selected.setState({ value: res.isMedicare })
            }

            const update = get().updateInterventions;
            if(update && cpt.emCalculator){
              update(cpt.emCalculator);
            }
          

          }, 200)
        }
      }
    },
    updateMinMax: (d: Driver) => {
      // console.log('updateMinMax raw', d)
      if (!d || isNaN(d.maxRISK)) {
        console.log('invalid calc min max')
        return
      }
      const num = (old: number, update: number) => (isNaN(update) ? old : update)

      const config: EmCalculatorConfiguration = get().config
      config.minHPI = num(config.minHPI, d.minHPI)
      config.maxHPI = num(config.maxHPI, d.maxHPI)
      config.minROS = num(config.minROS, d.minROS)
      config.maxROS = num(config.maxROS, d.maxROS)
      config.minPFSH = num(config.minPFSH, d.minPFSH)
      config.maxPFSH = num(config.maxPFSH, d.maxPFSH)
      config.minEXAM = num(config.minEXAM, d.minEXAM)
      config.maxEXAM = num(config.maxEXAM, d.maxEXAM)
      config.minOPT = num(config.minOPT, d.minOPT)
      config.maxOPT = num(config.maxOPT, d.maxOPT)
      config.minDATA = num(config.minDATA, d.minDATA)
      config.maxDATA = num(config.maxDATA, d.maxDATA)
      config.minRISK = num(config.minRISK, d.minRISK)
      config.maxRISK = num(config.maxRISK, d.maxRISK)

      set({ config, update: true })
    },
    calculateElements: async (v) => {
      const req = get().request
      v = { ...req, ...v, ...{ department: get().department } }

      if (get().v2 && v.emDriver && v.noteType) {
        const r = await calculateFromElementsRequest2({
          department: v.department,
          emDriver: v.emDriver,
          noteType: v.noteType,
          opt: v.opt as any,
          data: v.data as any,
          risk: v.risk as any,
          totalCriticalCareTime: v.totalCriticalCareTime,
          isMedicare: v.isMedicare,
        })

        if (r?.success) {
          r.result.suggestedEM = parseInt(r.result.suggestedEM as any)
          r.result.calculatedEM = r.result.suggestedEM

          const result = { ...req, ...r.result }
          set({ result })

          if (api.onResult) {
            api.onResult(result)
          }
        } else {
          set({ result: { errorMessages: r?.errors } as any })
        }
      } else {
        if (isValidElementRequest(v)) {
          set({ result: null })
          const r = await calculateFromElementsRequest(v)
          if (r?.success) {
            set({ result: { ...req, ...r.result } })
          } else {
            set({ result: { errorMessages: r?.errors } as any })
          }
        }
      }
    },
    calculateNopp: async (v) => {
      v = { ...get().request, ...v, ...{ department: get().department } }

      if (v.noppCategory && isValidRequest(v)) {
        set({ result: null })
        const r = await calculateFromElementsRequest(v)

        if (r?.success) {
          set({ result: r.result })
        } else {
          set({ result: { errorMessages: r?.errors } as any })
        }
      }
    },
    calculateTime: async (v) => {
      v = { ...get().request, ...v, ...{ department: get().department } }

      if (isValidTimeRangeRequest(v)) {
        set({ result: null })
        const r = await calculateFromTimeRequest(v)
        if (r?.success) {
          set({ result: r.result })
        } else {
          set({ result: { errorMessages: r?.errors } as any })
        }
      }
    },
    clear: () => {
      set({ result: {} as any, request: {} as any, reset: true })
    },
    setResult: (inputs: EmResult) => {
      set({ inputs })
    },
  }))
  return api
}
